import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Heart, Baby } from "@phosphor-icons/react";

export default function Onboarding() {
  const navigate = useNavigate();
  const [mode, setMode] = useState(null);
  const [busy, setBusy] = useState(false);

  const [preg, setPreg] = useState({ due_date: "", mother_name: "", father_name: "", is_high_risk: false, is_twin: false, is_ivf: false });
  const [baby, setBaby] = useState({ name: "", birth_date: "", gender: "", blood_group: "", birth_weight_kg: "" });

  const submitPregnancy = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/pregnancy/profile", preg);
      toast.success("Your pregnancy journey is set.");
      navigate("/app/dashboard");
    } catch (e) {
      toast.error("Could not save. Please try again.");
    } finally { setBusy(false); }
  };

  const submitBaby = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...baby, birth_weight_kg: baby.birth_weight_kg ? Number(baby.birth_weight_kg) : null };
      await api.post("/baby", payload);
      toast.success(`Welcome to the world, ${baby.name}.`);
      navigate("/app/dashboard");
    } catch (e) {
      toast.error("Could not save. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl mx-auto" data-testid="onboarding-page">
      <p className="overline text-saffron mb-3">Begin</p>
      <h1 className="font-serif font-semibold text-4xl sm:text-5xl mb-2">Where is your family today?</h1>
      <p className="text-ink-700 mb-10">Choose one — Vansha will gently shape itself around your story. You can always add the other later.</p>

      {!mode && (
        <div className="grid sm:grid-cols-2 gap-6">
          <button onClick={() => setMode("pregnancy")} className="vansha-card p-8 text-left hover:shadow-lg transition" data-testid="onboard-pregnancy">
            <Heart weight="thin" size={36} className="text-terracotta mb-4" />
            <h3 className="font-serif text-2xl">We are expecting</h3>
            <p className="text-ink-700 mt-2 text-sm">Set up your pregnancy operating system — week-by-week guidance, scans, journal, family.</p>
          </button>
          <button onClick={() => setMode("baby")} className="vansha-card p-8 text-left hover:shadow-lg transition" data-testid="onboard-baby">
            <Baby weight="thin" size={36} className="text-terracotta mb-4" />
            <h3 className="font-serif text-2xl">Our baby is here</h3>
            <p className="text-ink-700 mt-2 text-sm">Create your baby's digital twin — milestones, vaccinations, memories, growth.</p>
          </button>
        </div>
      )}

      {mode === "pregnancy" && (
        <form onSubmit={submitPregnancy} className="space-y-7 vansha-card p-8" data-testid="form-pregnancy">
          <h2 className="font-serif text-2xl">Your pregnancy</h2>
          <div>
            <label className="overline mb-2 block">Due date</label>
            <input type="date" required value={preg.due_date} onChange={(e) => setPreg({ ...preg, due_date: e.target.value })} className="input-line" data-testid="preg-due-date" />
          </div>
          <div>
            <label className="overline mb-2 block">Mother's name (optional)</label>
            <input type="text" value={preg.mother_name} onChange={(e) => setPreg({ ...preg, mother_name: e.target.value })} className="input-line" data-testid="preg-mother-name" />
          </div>
          <div>
            <label className="overline mb-2 block">Partner's name (optional)</label>
            <input type="text" value={preg.father_name} onChange={(e) => setPreg({ ...preg, father_name: e.target.value })} className="input-line" data-testid="preg-father-name" />
          </div>
          <div className="flex flex-wrap gap-4 text-sm">
            <label className="flex items-center gap-2"><input type="checkbox" checked={preg.is_high_risk} onChange={(e) => setPreg({ ...preg, is_high_risk: e.target.checked })} data-testid="preg-high-risk" /> High risk pregnancy</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={preg.is_twin} onChange={(e) => setPreg({ ...preg, is_twin: e.target.checked })} data-testid="preg-twin" /> Expecting twins</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={preg.is_ivf} onChange={(e) => setPreg({ ...preg, is_ivf: e.target.checked })} data-testid="preg-ivf" /> IVF pregnancy</label>
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={() => setMode(null)} className="btn-ghost" data-testid="preg-back">Back</button>
            <button type="submit" disabled={busy} className="btn-primary flex-1" data-testid="preg-submit">{busy ? "Saving…" : "Set up Vansha"}</button>
          </div>
        </form>
      )}

      {mode === "baby" && (
        <form onSubmit={submitBaby} className="space-y-7 vansha-card p-8" data-testid="form-baby">
          <h2 className="font-serif text-2xl">Your baby</h2>
          <div><label className="overline mb-2 block">Baby's name</label><input type="text" required value={baby.name} onChange={(e) => setBaby({ ...baby, name: e.target.value })} className="input-line" data-testid="baby-name" /></div>
          <div><label className="overline mb-2 block">Birth date</label><input type="date" required value={baby.birth_date} onChange={(e) => setBaby({ ...baby, birth_date: e.target.value })} className="input-line" data-testid="baby-birth-date" /></div>
          <div className="grid grid-cols-2 gap-6">
            <div><label className="overline mb-2 block">Gender</label>
              <select value={baby.gender} onChange={(e) => setBaby({ ...baby, gender: e.target.value })} className="input-line bg-transparent" data-testid="baby-gender">
                <option value="">—</option><option value="girl">Girl</option><option value="boy">Boy</option><option value="other">Other</option>
              </select>
            </div>
            <div><label className="overline mb-2 block">Blood group</label><input type="text" value={baby.blood_group} onChange={(e) => setBaby({ ...baby, blood_group: e.target.value })} className="input-line" placeholder="O+, A+ …" data-testid="baby-blood-group" /></div>
          </div>
          <div><label className="overline mb-2 block">Birth weight (kg)</label><input type="number" step="0.1" value={baby.birth_weight_kg} onChange={(e) => setBaby({ ...baby, birth_weight_kg: e.target.value })} className="input-line" placeholder="3.2" data-testid="baby-weight" /></div>
          <div className="flex gap-3">
            <button type="button" onClick={() => setMode(null)} className="btn-ghost" data-testid="baby-back">Back</button>
            <button type="submit" disabled={busy} className="btn-primary flex-1" data-testid="baby-submit">{busy ? "Saving…" : "Add baby"}</button>
          </div>
        </form>
      )}
    </div>
  );
}
